import { Avatar, Body } from '@assembly-js/design-system';
import { Popover } from '@mui/material';
import type { ICellRendererParams } from 'ag-grid-community';
import clsx from 'clsx';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { ClientTableRowModel, CompanyCellData } from 'src/store/clients/types';

interface CompanyCellRendererProps extends ICellRendererParams {
  data: ClientTableRowModel;
  value: CompanyCellData[];
}
const AvatarClasses = 'border rounded-[3px] border-gray-200';
const ComboAvatars = ({ companies }: { companies: CompanyCellData[] }) => {
  return (
    <div className="h-5 w-5 relative">
      {companies.map((company, index) => (
        <Avatar
          key={company.id}
          className={clsx(AvatarClasses, ' w-[14px] h-[14px] absolute', {
            hidden: index > 1, // Only show first two avatars
            'left-0 top-0': index === 0, // First avatar is on the left
            'right-0 bottom-0': index === 1, // Second avatar is on the right
          })}
          text={company.name.toUpperCase().charAt(0)}
          fallbackColor={company.fallbackColor}
          src={company.avatarUrl}
          size="sm"
          variant="rounded"
        />
      ))}
    </div>
  );
};

const CompanyCellAvatarItem = ({ company }: { company: CompanyCellData }) => {
  return (
    <div className="flex items-center h-8">
      <Avatar
        className={clsx(AvatarClasses, 'w-[18px] h-[18px]')}
        text={company.name.toUpperCase().charAt(0)}
        fallbackColor={company.fallbackColor}
        src={company.avatarUrl}
        size="sm"
        variant="rounded"
      />
      <Body size="sm" className="text-black-heading ml-3">
        {company.name}
      </Body>
    </div>
  );
};

export const CompanyCellRenderer: React.FC<CompanyCellRendererProps> =
  React.memo(({ value: companies }) => {
    const { t } = useTranslation();
    const [isPopoverOpen, setIsPopoverOpen] = React.useState(false);
    const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(
      null,
    );

    const companyCount = companies.length;
    if (
      companyCount === 0 ||
      (companyCount === 1 && companies.at(0)?.isPlaceholder)
    ) {
      return <span>&mdash;</span>;
    }

    const singleCompany = companies.length === 1 ? companies.at(0) : null;

    return (
      <>
        <button
          className={
            'p-0 appearance-none border-none bg-transparent flex items-center justify-start relative cursor-pointer'
          }
          onClickCapture={(e) => {
            if (companyCount > 1) {
              e.nativeEvent.stopPropagation();
              setIsPopoverOpen(true);
            }
          }}
          ref={setAnchorEl}
        >
          {companyCount > 1 && <ComboAvatars companies={companies} />}
          {singleCompany && <CompanyCellAvatarItem company={singleCompany} />}
          {companyCount > 1 && (
            <div>
              <div
                className={clsx('text-black-heading leading-[21px] ml-3', {
                  'underline decoration-dotted': companyCount > 1,
                })}
              >
                {t(($) => $.company_count, {
                  count: companyCount ?? 0,
                })}
              </div>
            </div>
          )}
        </button>
        {companyCount > 1 && (
          <Popover
            open={isPopoverOpen}
            onClose={() => setIsPopoverOpen(false)}
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
          >
            <div className="flex flex-col gap-0.5 px-2 py-1">
              {companies.map((company) => (
                <CompanyCellAvatarItem key={company.id} company={company} />
              ))}
            </div>
          </Popover>
        )}
      </>
    );
  });
